/** The Today card for work waiting on this phone, and the animal page's sessions section. */
import { Link } from "react-router";
import type { AnimalDetail } from "../../../contract";
import { api } from "../../../web/lib/api";
import { plural, t, useLanguage } from "../../../web/lib/i18n";
import { invalidateAll } from "../../../web/lib/query";
import { useCan } from "../../../web/lib/session";
import { Button, Card, Chip, Icon, List, Row, toast } from "../../../web/ui/kit";
import { useSessionsOwner } from "./common";
import { flushNow, useOnline, useQueued, useStoredPacks, useSyncState } from "./offline";

/** Quiet unless chute results are waiting on this phone. */
export function PendingSyncCard() {
  useSessionsOwner();
  useLanguage();
  const queued = useQueued();
  const packs = useStoredPacks();
  const online = useOnline();
  const sync = useSyncState();
  const waiting = queued.filter((q) => q.state === "saved" || q.state === "sending");
  const stuck = queued.filter((q) => q.state === "blocked" || q.state === "failed");
  if (!waiting.length && !stuck.length) return null;
  const jobs = [...new Set([...waiting, ...stuck].map((q) => q.job_id))];
  const title = (id: string) => packs.find((p) => p.job_id === id)?.job.title ?? t("Field session");
  return (
    <Card className="sessions-today" tone={stuck.length ? "warn" : "info"}>
      <div className="sessions-today-head">
        <Icon name="phone" size={22} />
        <div>
          <strong>{waiting.length ? plural(waiting.length, "{{count}} chute result saved on this phone", "{{count}} chute results saved on this phone") : plural(stuck.length, "{{count}} chute result can't be sent", "{{count}} chute results can't be sent")}</strong>
          <p className="muted">{stuck.length ? t("Open the session to see why. Nothing has been lost.") : !online ? t("They'll send when there's signal.") : sync.lastStop === "auth" ? t("Sign in again to send them.") : sync.sending ? t("Sending now.") : t("Waiting to send.")}</p>
        </div>
        {online && waiting.length > 0 && <Button size="sm" busy={sync.sending} onClick={() => void flushNow()}>{t("Send now")}</Button>}
      </div>
      <List>{jobs.map((id) => <Row key={id} icon="scale" to={`/work/jobs/${id}`} title={title(id)} meta={plural(queued.filter((q) => q.job_id === id).length, "{{count}} waiting", "{{count}} waiting")} />)}</List>
    </Card>
  );
}

export const showSessionsSection = (a: AnimalDetail): boolean =>
  a.attrs["sessions.provisional"] === true || a.jobs.some((j) => j.workspace === "sessions");

/** On the animal page: added at the chute and not yet confirmed, and the sessions it's on the list for. */
export function AnimalSessions({ animal }: { animal: AnimalDetail }) {
  useLanguage();
  const can = useCan();
  const provisional = animal.attrs["sessions.provisional"] === true;
  const sessionId = typeof animal.attrs["sessions.session_id"] === "string" ? (animal.attrs["sessions.session_id"] as string) : null;
  const sessions = animal.jobs.filter((j) => j.workspace === "sessions");
  const confirm = async () => {
    await api.patch(`/animals/${animal.id}`, { attrs: { "sessions.provisional": false } });
    await invalidateAll();
    toast(t("Identity confirmed"), { tone: "good" });
  };
  return (
    <div className="stack">
      {provisional && (
        <Card tone="warn" className="sessions-provisional">
          <p><Icon name="warn" size={18} /> {t("Added at the chute from a tag Hondo didn't know. Check it isn't an animal you already have.")}</p>
          <div className="row-actions">
            {can("record") && <Button variant="primary" onClick={() => void confirm()}>{t("It's a new animal: confirm")}</Button>}
            {sessionId && <Button to={`/work/jobs/${sessionId}?tab=review`}>{t("Review in the session")}</Button>}
          </div>
        </Card>
      )}
      {sessions.length > 0 && (
        <List>
          {sessions.map((j) => (
            <Row key={j.id} icon="scale" to={`/work/jobs/${j.id}`} title={j.title} meta={t("{{done}} of {{total}} worked", { done: j.progress.done, total: j.progress.total })}
              trailing={can("record") ? <Link className="btn btn-sm btn-primary" to={`/work/sessions/${j.id}/run`}>{t("Chute mode")}</Link> : <Chip tone="neutral">{t("On the list")}</Chip>} />
          ))}
        </List>
      )}
    </div>
  );
}
